import { useCallback, useMemo, useState } from 'react';

import { DEFAULT_CURRENCY_CODE } from '../../../constants/locations';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useCalculateOrderPricingLazyQuery } from '../../../types/generated';
import { CountryCode } from '../../../types/Locations';
import { captureError } from '../../../utils/capture';
import {
  AmlPricedLine,
  exceedsAmlLimit,
  mostExpensiveLines,
} from '../utils/aml';

export type CheckoutAmlCheckArgs = {
  countryCode: CountryCode | null;
};

export type CheckoutAmlCheckController = {
  check: (lines: AmlPricedLine[], maxQuantity: number) => Promise<void>;
  required: boolean;
};

const allItems = (lines: AmlPricedLine[]) => {
  return lines.flatMap((line) => line.items);
};

export const useCheckoutAmlCheck = ({
  countryCode,
}: CheckoutAmlCheckArgs): CheckoutAmlCheckController => {
  const [preferredCountryCode] = useCountryCode();
  const [calculateOrderPricing] = useCalculateOrderPricingLazyQuery();
  const [required, setRequired] = useState(false);

  const priceInEuros = useCallback(
    async (items: { quantity: number; variantId: string }[]) => {
      const { data } = await calculateOrderPricing({
        variables: {
          countryCode: countryCode ?? preferredCountryCode,
          currencyCode: DEFAULT_CURRENCY_CODE,
          items,
        },
      });

      return data?.calculateOrderPricing;
    },
    [calculateOrderPricing, countryCode, preferredCountryCode],
  );

  const check = useCallback(
    async (lines: AmlPricedLine[], maxQuantity: number) => {
      setRequired(false);

      if (lines.length === 0) return;

      try {
        const priced = await priceInEuros(allItems(lines));

        if (!priced) return;

        if (lines.length <= maxQuantity) {
          setRequired(exceedsAmlLimit(priced));
          return;
        }

        const chargeableLines = mostExpensiveLines(
          lines,
          maxQuantity,
          priced.breakdown,
        );

        setRequired(
          exceedsAmlLimit(await priceInEuros(allItems(chargeableLines))),
        );
      } catch (error) {
        captureError(error);
      }
    },
    [priceInEuros],
  );

  return useMemo(() => ({ check, required }), [check, required]);
};
